/**
 * "These values are for this session. N are waiting to be accepted as official."
 *
 * Shown on a session's Values tab and on its results report. The links to the
 * place an approver accepts them (each item's Values tab) are drawn only for a
 * person who may accept values (§2.8 — no control the person may not use).
 */

import { useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useValueProposalStore } from '../../../stores/prioritization/valueProposalStore';
import { useItemsStore } from '../../../stores/prioritization/itemsStore';
import { useUserStore } from '../../../stores/prioritization/userStore';
import { sessionValuesWaitingLine, waitingByItem, waitingSessionContributions } from '../../../lib/sessionValuesWaiting';
import { useItemHref } from '@/lib/itemLinkScope';

export interface SessionValuesWaitingNoteProps {
  sessionId: string;
  /** Limit to these items (the session's items). */
  itemIds?: readonly string[];
  /** Hide the whole note when nothing is waiting (the results report). */
  hideWhenNoneWaiting?: boolean;
  /** Load value contributions on mount (when the host page does not). */
  load?: boolean;
}

export function SessionValuesWaitingNote({ sessionId, itemIds, hideWhenNoneWaiting = false, load = false }: SessionValuesWaitingNoteProps) {
  const itemHref = useItemHref();
  const proposals = useValueProposalStore((s) => s.proposals);
  const fetchValues = useValueProposalStore((s) => s.fetchFromSupabase);
  const items = useItemsStore((s) => s.items);
  const mayAccept = useUserStore((s) => s.can('values.accept'));

  useEffect(() => {
    if (load) void fetchValues();
  }, [load, fetchValues]);

  const waiting = useMemo(
    () => waitingSessionContributions(proposals ?? [], sessionId, itemIds),
    [proposals, sessionId, itemIds],
  );
  const perItem = useMemo(() => waitingByItem(waiting), [waiting]);

  if (hideWhenNoneWaiting && waiting.length === 0) return null;

  return (
    <div className="px-3 py-2 border border-[var(--rule)] bg-[var(--surface)] text-sm text-[var(--text-sec)] space-y-1" data-testid="session-values-waiting">
      <p>{sessionValuesWaitingLine(waiting.length)}</p>
      {mayAccept && perItem.length > 0 && (
        <p className="text-xs text-[var(--text-ter)]">
          Accept them on each item&apos;s Values tab:{' '}
          {perItem.map(({ itemId, count }, i) => {
            const title = items?.find((it) => it.id === itemId)?.title ?? itemId.slice(0, 8);
            return (
              <span key={itemId}>
                {i > 0 && ', '}
                <Link to={itemHref(itemId, 'values')} className="text-primary-600 hover:underline">
                  {title}
                </Link>
                {count > 1 && ` (${count})`}
              </span>
            );
          })}
        </p>
      )}
    </div>
  );
}
